(() => {
  if (customElements.get('nh-market-free-shipping-bar')) return;

  const CACHE_TTL = 60 * 1000;
  const CONFIG_CACHE =
    window.__nhMarketShippingConfigCache ||= new Map();

  const fallbackFormatMoney = (minor, currency) => {
    const amount = Number(minor) / 100;
    const locale =
      document.documentElement.lang ||
      navigator.language ||
      'en';
    const language =
      String(locale).toLowerCase().split('-')[0];

    if (!Number.isFinite(amount)) return '';

    const rounded =
      Math.round((amount + Number.EPSILON) * 100) / 100;
    const hasFraction =
      Math.abs(rounded - Math.trunc(rounded)) > 0.000001;

    let numberLocale = 'en-US';
    let token = currency;
    let position = 'prefix';

    switch (currency) {
      case 'TRY':
        numberLocale = 'tr-TR';
        token = 'TL';
        position = 'suffix';
        break;
      case 'AED':
        token = 'AED';
        position = language === 'ar' ? 'suffix' : 'prefix';
        break;
      case 'EGP':
        token = 'EGP';
        position = language === 'ar' ? 'suffix' : 'prefix';
        break;
      case 'SAR':
        token = 'SAR';
        position = language === 'ar' ? 'suffix' : 'prefix';
        break;
      case 'AZN':
        numberLocale = 'az-AZ';
        token = '₼';
        position = 'suffix';
        break;
      case 'RUB':
        numberLocale = 'ru-RU';
        token = '₽';
        position = 'suffix';
        break;
      case 'EUR':
        numberLocale =
          language === 'fr' ? 'fr-FR' :
          language === 'es' ? 'es-ES' :
          language === 'it' ? 'it-IT' :
          language === 'de' ? 'de-DE' :
          language === 'en' ? 'en-US' :
          'de-DE';
        token = '€';
        position = language === 'en'
          ? 'prefix-tight'
          : 'suffix';
        break;
      default:
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: hasFraction ? 2 : 0,
          maximumFractionDigits: 2
        }).format(rounded);
    }

    const number = new Intl.NumberFormat(numberLocale, {
      useGrouping: true,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2
    }).format(rounded);

    if (position === 'prefix-tight') {
      return `${token}${number}`;
    }

    if (position === 'prefix') {
      return `${token}\u00A0${number}`;
    }

    return `${number}\u00A0${token}`;
  };

  class NhMarketFreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.refresh();
    }

    async refresh() {
      const country = (this.dataset.country || '')
        .trim()
        .toUpperCase();

      const cartCurrency = (this.dataset.currency || '')
        .trim()
        .toUpperCase();

      const totalMinor = Number.parseInt(
        this.dataset.totalPrice || '0',
        10
      );

      const configUrl = this.dataset.configUrl;

      this.hidden = true;

      if (
        !country ||
        !cartCurrency ||
        !configUrl ||
        !Number.isFinite(totalMinor)
      ) {
        return;
      }

      try {
        const config = await this.getConfig(
          configUrl,
          country
        );

        const thresholdMajor =
          Number(config?.freeShippingThreshold);

        const configCurrency =
          String(config?.currencyCode || '')
            .toUpperCase();

        if (
          !Number.isFinite(thresholdMajor) ||
          thresholdMajor <= 0
        ) {
          return;
        }

        if (
          !configCurrency ||
          configCurrency !== cartCurrency
        ) {
          return;
        }

        const thresholdMinor =
          Math.round(thresholdMajor * 100);

        const safeTotal =
          Math.max(0, totalMinor);

        const remainingMinor =
          Math.max(
            0,
            thresholdMinor - safeTotal
          );

        const progress =
          Math.min(
            100,
            Math.max(
              0,
              (safeTotal / thresholdMinor) * 100
            )
          );

        const reached =
          remainingMinor === 0;

        this.style.setProperty(
          '--nh-free-shipping-progress',
          `${progress}%`
        );

        this.classList.toggle(
          'is-reached',
          reached
        );

        this.hidden = false;

        const progressEl =
          this.querySelector('[data-nh-progress]');
        const percentEl =
          this.querySelector('[data-nh-percent]');
        const messageEl =
          this.querySelector('[data-nh-message]');

        if (progressEl) {
          progressEl.setAttribute(
            'aria-valuenow',
            String(Math.round(progress))
          );

          progressEl.setAttribute(
            'aria-valuetext',
            `${Math.round(progress)}%`
          );
        }

        if (percentEl) {
          percentEl.textContent =
            `${Math.round(progress)}%`;
        }

        if (messageEl) {
          if (reached) {
            messageEl.innerHTML =
              this.dataset.reachedMessage || '';
          } else {
            const amount =
              this.escapeHtml(
                this.formatMoney(
                  remainingMinor,
                  configCurrency
                )
              );

            const template =
              this.dataset.unreachedMessage || '';

            messageEl.innerHTML =
              template.replaceAll(
                '__NH_AMOUNT__',
                amount
              );
          }
        }
      } catch (error) {
        console.warn(
          '[Nazik Home] Free shipping config could not be loaded.',
          error
        );

        this.hidden = true;
      }
    }

    async getConfig(baseUrl, country) {
      const key =
        `${baseUrl}|${country}`;

      const now = Date.now();
      const cached =
        CONFIG_CACHE.get(key);

      if (
        cached &&
        now - cached.at < CACHE_TTL
      ) {
        return cached.data;
      }

      const response = await fetch(
        `${baseUrl}?country=${encodeURIComponent(country)}`,
        {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Shipping config HTTP ${response.status}`
        );
      }

      const data =
        await response.json();

      CONFIG_CACHE.set(key, {
        at: now,
        data
      });

      return data;
    }

    formatMoney(minor, currency) {
      if (
        window.NHMoney &&
        typeof window.NHMoney.formatMinor === 'function'
      ) {
        const formatted =
          window.NHMoney.formatMinor(
            minor,
            currency
          );

        if (formatted) return formatted;
      }

      return fallbackFormatMoney(
        minor,
        currency
      );
    }

    escapeHtml(value) {
      return String(value).replace(
        /[&<>'"]/g,
        (char) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          "'": '&#39;',
          '"': '&quot;'
        })[char]
      );
    }
  }

  customElements.define(
    'nh-market-free-shipping-bar',
    NhMarketFreeShippingBar
  );
})();